import React, { memo, useCallback, useEffect } from 'react';
import { FeatureFlagEnum, hasFeatureFlag, TripType, useGlobalContext, useSearch, setLastViewedEstab, currentTripStateIdVar, updateLastSearch, useHotelBasketSummary, useFlightSearchResults, HotelAvailabilityErrors, useRecordPriceSummaryToCalendar, useRecordPriceSrpToSummary, recordSummaryPriceSideEffect, } from '@honeycomb/data';
import { Container, useBreakpoints, Styler, FlexGrid, Stacker, usePrevious, Scrollspy, ScrollspyButton, ScrollspyNavigation, ScrollspyTarget, useDisclosure, LazyLoadComponent, Paper, toastsStore, toasts, } from '@honeycomb/ui-core';
import { getRoomBoardIdParameter, reduceMockErrorAttemptsRouting, removeBoardIdParameter } from '@honeycomb/routing';
import dynamic from 'next/dynamic';
import { FunnelAction, trackFunnelAction, BetaEstabAction, trackBetaEstabAction, pushHotelBasketSummary, SyncSponsoredListingClickUrl, pushPreSelectedFlight, pushBoardSelection, addDatadogRumAction, } from '@honeycomb/tracking';
import { HotelSummary, HotelMobileFooterBar } from '../HotelSummary';
import { HotelRoomBoardSelector } from '../HotelRoomBoardSelector';
import { HotelFlightsSummary } from '../HotelFlightsSummary/HotelFlightsSummary';
import { HotelBasketActionType, useHotelBasketContext } from '../HotelBasketProvider';
import { HotelStaticRooms } from '../HotelStaticRooms';
import { HotelReviewsPageSection } from '../HotelReviews';
import { HotelOffers } from '../HotelOffers';
import { HotelOverview } from '../HotelOverview';
import { SimilarHotels } from '../SimilarHotels/SimilarHotels';
import { WeatherFlightRoute } from '../WeatherRoute/WeatherFlightRoute';
import { HotelPriceCalendar } from '../HotelPriceCalendar/HotelPriceCalendar';
import { LandingPagePriceCalendar } from '../LandingPagePriceCalendar';
import { HotelHeadline } from '../HotelSummary/HotelHeadline';
import { useHotelDetailsContext } from './HotelDetails.context';
import { HotelDetailsActionType } from './HotelDetailsReducer';
import { useHotelDetailsPageSectionOrder } from './useHotelDetailsPageSectionOrder';
import { csvContainsAny, getHotelImageUrl, getTotalDeltaPrice, isDestinationEnabledInConfig, isValidEstablishmentPageQuery, parseBoolean, StoredAirportKeys, useLocalStorage, useMountEffect, useStoredAirport, useTripTypeFromTrip, } from '../../../utils';
import { backgroundFlightSearchConditions, hasPartialFlightData, skipHotelFlightSearchResults, } from '../../background-flight-search/utils';
import { HotelFlightUpsell } from '../HotelFlightUpsell';
import { NightsAdjustedNotification } from '../NightsAdjustedNotification';
import { HotelImageGallery } from '../HotelImageGallery';
import { HotelReviewsDialog } from '../HotelReviewsDialog';
import { getHotelLocation, isBoardUpgraded } from '../utils';
import { HotelDetailsRichResult } from './HotelDetailsRichResult';
const SUMMARY_COLUMN_MIN_WIDTH = '375px';
const HotelFlightDrawer = dynamic(() => import('../HotelFlightsDrawer').then((module) => module.HotelFlightsDrawer), { ssr: false });
const HotelFlightsDrawerMemo = memo(HotelFlightDrawer);
const HotelStaticMap = dynamic(() => import('../HotelStaticMap').then((module) => module.HotelStaticMap), {
    ssr: false,
});
export function HotelDetails({ hotelDetails, pageVariant, similarHotelsData, weatherRouteDetails, onTracking, onSearchUnitOpenChange, }) {
    var _a, _b, _c, _d, _e, _f, _g;
    const { configSettings: { HCEnablePricingCalendar, DisableCustomerReviews, HotelImageCDN, ProductCacheEnabled, EnableEstabOffers, EnableMockErrors, EnableFlightUpsell, FlightUpsellDestinations, EnableHotelUrgencyMessaging, UrgencyMessageDestinationEnabled, }, resourceStrings: { HOTEL_DETAILS_SCROLLSPY_SUMMARY, HOTEL_DETAILS_SCROLLSPY_PACKAGE, HOTEL_DETAILS_SCROLLSPY_FLIGHTS, HOTEL_DETAILS_SCROLLSPY_ROOMS, HOTEL_DETAILS_SCROLLSPY_OVERVIEW, HOTEL_DETAILS_SCROLLSPY_REVIEWS, HOTEL_DETAILS_SCROLLSPY_MAP, HOTEL_DETAILS_SCROLLSPY_FLIGHTUPSELL, }, } = useGlobalContext();
    const isCacheEnabled = parseBoolean(ProductCacheEnabled);
    const { estabId, customerFeedback, starRating, name, geography, isCached } = hotelDetails;
    const isLandingPage = pageVariant === 'landing-page';
    const { hotelBasket, dispatch } = useHotelBasketContext();
    const { state: { showPricingCalendar, pricingCalendarInitialLoad }, dispatch: detailsDispatch, } = useHotelDetailsContext();
    const { selectedFlight, selectedRooms, tripStateId: flightTripStateId, availabilityErrors, hotelCheckInDate, waitingForFlights, waitingForRooms, } = hotelBasket;
    const continueToBookError = availabilityErrors.includes(HotelAvailabilityErrors.CONTINUE_TO_BOOK_ERROR);
    const cachedNavlError = availabilityErrors.includes(HotelAvailabilityErrors.CACHED_NAVL_ERROR);
    // Scrollspy
    const scrollspyRef = React.useRef(null);
    const handleScrollToSection = React.useCallback((target) => {
        var _a;
        (_a = scrollspyRef.current) === null || _a === void 0 ? void 0 : _a.scrollToTarget(target);
    }, []);
    useEffect(() => {
        if (continueToBookError || cachedNavlError) {
            const timeoutId = setTimeout(() => {
                handleScrollToSection === null || handleScrollToSection === void 0 ? void 0 : handleScrollToSection('rooms');
            }, 500);
            // Cleanup function clears the timeout
            return () => clearTimeout(timeoutId);
        }
        return undefined;
    }, [handleScrollToSection, continueToBookError, cachedNavlError, detailsDispatch]);
    useEffect(() => {
        if (!isLandingPage)
            SyncSponsoredListingClickUrl(estabId);
    }, [estabId, isLandingPage]);
    // Flights drawer
    const [flightsDrawerOpen, { open: handleOpenFlightsDrawer, close: handleCloseFlightsDrawer }] = useDisclosure(false);
    const [flightsDrawerExited, setFlightsDrawerExited] = React.useState(false);
    // Search
    const [searchState] = useSearch([]);
    const { tripStateId, rooms } = searchState;
    const tripType = useTripTypeFromTrip();
    const currentTripStateIdVariable = currentTripStateIdVar();
    const prevTripState = usePrevious(currentTripStateIdVariable);
    const requiresBackgroundFlightSearch = backgroundFlightSearchConditions(searchState, isCacheEnabled);
    const flightSearchResultsSideEffect = useCallback((data) => {
        var _a, _b;
        if (!(data === null || data === void 0 ? void 0 : data.flightSearchResults)) {
            return;
        }
        const preSelectedFlight = (_a = data.flightSearchResults.flights) === null || _a === void 0 ? void 0 : _a.find((flight) => flight.isPreSelected);
        // Once the flight search results is done, and I need to populate additional data, get the pre-selected flight from
        // flight search results and pop that in the context to fill gaps in data. TR Cached single destination case.
        const isWaitingForAdditionalData = hasPartialFlightData(selectedFlight) && !requiresBackgroundFlightSearch;
        if (preSelectedFlight && isWaitingForAdditionalData) {
            const { flightId, inboundLeg, outboundLeg, deltaPrice } = preSelectedFlight;
            dispatch({
                type: HotelBasketActionType.SET_SELECTED_FLIGHT,
                payload: {
                    deltaPrice: ((_b = selectedFlight === null || selectedFlight === void 0 ? void 0 : selectedFlight.deltaPrice) !== null && _b !== void 0 ? _b : 0) + getTotalDeltaPrice(deltaPrice !== null && deltaPrice !== void 0 ? deltaPrice : 0, rooms),
                    flightId,
                    inboundLeg,
                    outboundLeg,
                },
            });
        }
    }, [dispatch, requiresBackgroundFlightSearch, rooms, selectedFlight]);
    const [results] = useFlightSearchResults([pushPreSelectedFlight], hotelCheckInDate, '', skipHotelFlightSearchResults(selectedFlight, flightTripStateId, hotelCheckInDate), flightTripStateId, parseInt(estabId));
    // Moving side effects to a separate useEffect to ensure
    // that they are executed when results of the hook come from cache as oncompleted does not
    // get called in that case.
    useEffect(() => {
        var _a;
        if ((_a = results === null || results === void 0 ? void 0 : results.data) === null || _a === void 0 ? void 0 : _a.flightSearchResults) {
            console.count('FlightSearchResults side effect');
            flightSearchResultsSideEffect(results.data);
        }
    }, [flightSearchResultsSideEffect, results.data]);
    // Reviews dialog
    const [reviewsDialogOpen, { open: handleOpenReviewsDialog, close: handleCloseReviewsDialog }] = useDisclosure(false);
    const [reviewsDialogExited, setReviewsDialogExited] = React.useState(false);
    const [reviewsOrigin, setReviewsOrigin] = React.useState('tripadvisor');
    const showHotelOffers = isLandingPage && isCacheEnabled && parseBoolean(EnableEstabOffers) && hotelDetails.isCached;
    useEffect(() => {
        var _a;
        if (currentTripStateIdVariable) {
            updateLastSearch(searchState);
            setLastViewedEstab({
                estabId,
                starRating,
                imageUrl: getHotelImageUrl(HotelImageCDN, estabId),
                tripStateId,
                name,
                location: (_a = (geography && getHotelLocation(geography))) !== null && _a !== void 0 ? _a : '',
            });
        }
    }, [HotelImageCDN, currentTripStateIdVariable, estabId, geography, name, searchState, starRating, tripStateId]);
    const dispatchHotelRooms = (data) => {
        var _a, _b;
        const boardOptionsQueryParam = (_a = getRoomBoardIdParameter()) === null || _a === void 0 ? void 0 : _a.toString().split(',');
        if (boardOptionsQueryParam === null || boardOptionsQueryParam === void 0 ? void 0 : boardOptionsQueryParam.length) {
            const { hotelRoomsAvailability } = data;
            // Contains all the room/board options which are available.
            const availableRooms = (_b = hotelRoomsAvailability === null || hotelRoomsAvailability === void 0 ? void 0 : hotelRoomsAvailability.boardOptions) === null || _b === void 0 ? void 0 : _b.flatMap((bo) => bo.requestedRooms).flatMap((roomsAvailable) => roomsAvailable === null || roomsAvailable === void 0 ? void 0 : roomsAvailable.availableRooms);
            const boardOptions = availableRooms === null || availableRooms === void 0 ? void 0 : availableRooms.filter((room) => { var _a; return room && boardOptionsQueryParam.includes((_a = room === null || room === void 0 ? void 0 : room.roomId) !== null && _a !== void 0 ? _a : ''); });
            if ((boardOptions === null || boardOptions === void 0 ? void 0 : boardOptions.length) && boardOptions.length === rooms.length) {
                const newlySelectedRooms = boardOptions.map((bo) => {
                    var _a;
                    return {
                        boardRoomId: bo.roomId,
                        boardType: bo.pricingSummary.boardType,
                        boardTypeDescription: (_a = bo.pricingSummary) === null || _a === void 0 ? void 0 : _a.boardTypeDescription,
                        pricingSummary: bo.pricingSummary,
                        roomDescription: bo.masterRoom.title,
                    };
                });
                // Tracking update
                pushBoardSelection(estabId, newlySelectedRooms[0].boardTypeDescription, isBoardUpgraded(hotelRoomsAvailability, newlySelectedRooms[0].boardType));
                dispatch({
                    type: HotelBasketActionType.SET_SELECTED_ROOM,
                    payload: { selectedRooms: newlySelectedRooms },
                });
            }
            else {
                // Remove the query param if the one they chosen is not available or invalid.
                removeBoardIdParameter();
            }
        }
    };
    const setRoomsAvailabilityError = () => {
        trackBetaEstabAction(BetaEstabAction.AVAILABILITY_ERROR_ROOMS, tripType !== null && tripType !== void 0 ? tripType : 'Unknown');
        dispatch({
            type: HotelBasketActionType.SET_AVAILABILITY_ERRORS,
            payload: HotelAvailabilityErrors.ROOM_ERROR,
        });
    };
    const setBasketAvailabilityError = () => {
        trackBetaEstabAction(BetaEstabAction.AVAILABILITY_ERROR_BASKET_SUMMARY, tripType !== null && tripType !== void 0 ? tripType : 'Unknown');
        dispatch({
            type: HotelBasketActionType.SET_AVAILABILITY_ERRORS,
            payload: HotelAvailabilityErrors.BASKET_ERROR,
        });
        if (parseBoolean(EnableMockErrors)) {
            reduceMockErrorAttemptsRouting();
        }
    };
    const dispatchBasketDetails = (data) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        const hasFlightId = (_b = (_a = data.hotelBasketSummary) === null || _a === void 0 ? void 0 : _a.flightDetails) === null || _b === void 0 ? void 0 : _b.flightId;
        if (hotelBasket.selectedRooms.length === 0) {
            pushBoardSelection(estabId, (_f = (_e = (_d = (_c = data.hotelBasketSummary) === null || _c === void 0 ? void 0 : _c.roomDetails) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.boardTypeDescription) !== null && _f !== void 0 ? _f : '', isBoardUpgraded(data.hotelRoomsAvailability, (_j = (_h = (_g = data.hotelBasketSummary) === null || _g === void 0 ? void 0 : _g.roomDetails) === null || _h === void 0 ? void 0 : _h[0]) === null || _j === void 0 ? void 0 : _j.boardType));
            dispatch({
                type: HotelBasketActionType.SET_SELECTED_ROOM,
                payload: { selectedRooms: (_l = (_k = data.hotelBasketSummary) === null || _k === void 0 ? void 0 : _k.roomDetails) !== null && _l !== void 0 ? _l : [] },
            });
        }
        if (!requiresBackgroundFlightSearch) {
            dispatch({
                type: HotelBasketActionType.SET_TRIP_STATE_ID,
                payload: tripStateId !== null && tripStateId !== void 0 ? tripStateId : '',
            });
        }
        if (!hasFlightId && tripType === TripType.Holiday && !requiresBackgroundFlightSearch) {
            trackBetaEstabAction(BetaEstabAction.AVAILABILITY_ERROR_NO_FLIGHT_IN_TRIP, tripType !== null && tripType !== void 0 ? tripType : 'Unknown');
            dispatch({
                type: HotelBasketActionType.SET_AVAILABILITY_ERRORS,
                payload: HotelAvailabilityErrors.FLIGHT_ERROR_NO_FLIGHT_ID,
            });
        }
        if (hasFlightId && ((_m = data.hotelBasketSummary) === null || _m === void 0 ? void 0 : _m.flightDetails) && !requiresBackgroundFlightSearch) {
            dispatch({
                type: HotelBasketActionType.SET_SELECTED_FLIGHT,
                payload: Object.assign(Object.assign({}, data.hotelBasketSummary.flightDetails), { deltaPrice: 0 }),
            });
        }
        if ((_o = data.hotelBasketSummary) === null || _o === void 0 ? void 0 : _o.checkInDate) {
            dispatch({
                type: HotelBasketActionType.SET_HOTEL_CHECK_IN_DATE,
                payload: (_p = data.hotelBasketSummary) === null || _p === void 0 ? void 0 : _p.checkInDate,
            });
        }
    };
    const hotelSummaryResult = useHotelBasketSummary(estabId, [
        dispatchBasketDetails,
        dispatchHotelRooms,
        pushHotelBasketSummary,
        (data) => recordSummaryPriceSideEffect(data, estabId, isCached !== null && isCached !== void 0 ? isCached : false),
    ], [setBasketAvailabilityError, setRoomsAvailabilityError], !(tripType === TripType.Holiday || tripType === TripType.Hotel));
    // // Cached Navl retry logic
    useEffect(() => {
        if (cachedNavlError) {
            hotelSummaryResult.refetch();
            trackFunnelAction(FunnelAction.REFETCH_AVAILABILITY, tripType !== null && tripType !== void 0 ? tripType : 'Unknown');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cachedNavlError]);
    const { roomDetails, stopSellMessage, isNumberOfNightsAdjusted } = (_b = (_a = hotelSummaryResult.data) === null || _a === void 0 ? void 0 : _a.hotelBasketSummary) !== null && _b !== void 0 ? _b : {};
    const isSponsored = (roomDetails === null || roomDetails === void 0 ? void 0 : roomDetails.some((room) => { var _a; return (_a = room === null || room === void 0 ? void 0 : room.pricingSummary) === null || _a === void 0 ? void 0 : _a.isSponsored; })) || false;
    const { largeUp } = useBreakpoints();
    const isMobileLayout = !largeUp;
    // Reviews
    const reviewsEnabled = !parseBoolean(DisableCustomerReviews);
    const numberOfReviews = (_c = customerFeedback === null || customerFeedback === void 0 ? void 0 : customerFeedback.numberOfReviews) !== null && _c !== void 0 ? _c : 0;
    const showReviews = reviewsEnabled && numberOfReviews > 0;
    const pricingCalendarEnabled = parseBoolean(HCEnablePricingCalendar) || hasFeatureFlag(FeatureFlagEnum.PRICING_CALENDAR);
    // To show the calendar the config needs to be on, and if its landing page then estab also needs to be cached.
    const displayPricingCalendar = pricingCalendarEnabled && (isLandingPage ? hotelDetails.isCached : true);
    const isWaitingForSelection = tripType === TripType.Holiday ? waitingForFlights || waitingForRooms : waitingForRooms;
    const hasRoomsAvailable = selectedRooms.length > 0;
    const hasNoRoomsAvailable = !isWaitingForSelection && !hasRoomsAvailable;
    const showRoomsComponent = !availabilityErrors.includes(HotelAvailabilityErrors.BASKET_ERROR) && !hasNoRoomsAvailable;
    const showFlightsSection = !isLandingPage && tripType === TripType.Holiday && showRoomsComponent;
    const hasMatchingTabDestinations = React.useMemo(() => {
        var _a, _b, _c;
        const destinationIds = isCacheEnabled
            ? (_b = (_a = searchState.destinations) === null || _a === void 0 ? void 0 : _a.flatMap((dest) => { var _a; return (_a = dest === null || dest === void 0 ? void 0 : dest.id) !== null && _a !== void 0 ? _a : []; })) !== null && _b !== void 0 ? _b : []
            : (_c = searchState.destinationIds) !== null && _c !== void 0 ? _c : [];
        return csvContainsAny(FlightUpsellDestinations, destinationIds);
    }, [FlightUpsellDestinations, searchState.destinationIds, searchState.destinations, isCacheEnabled]);
    const showFlightUpsell = (hasMatchingTabDestinations || parseBoolean(EnableFlightUpsell)) &&
        !isLandingPage &&
        tripType === TripType.Hotel &&
        showRoomsComponent;
    // Flights local storage
    const [_storedDestinationAirport, setStoredDestinationAirport] = useStoredAirport(StoredAirportKeys.DESTINATION);
    const [_storedDepartureAirport, setStoredDepartureAirport] = useStoredAirport(StoredAirportKeys.DEPARTURE);
    const [_connectingFlight, setConnectingFlight] = useLocalStorage('connectingFlight');
    const isRoomAvailable = Boolean((_e = (_d = hotelSummaryResult === null || hotelSummaryResult === void 0 ? void 0 : hotelSummaryResult.data) === null || _d === void 0 ? void 0 : _d.hotelBasketSummary) === null || _e === void 0 ? void 0 : _e.roomsCount);
    const showNightsAdjustedNotification = !isLandingPage && isNumberOfNightsAdjusted && !stopSellMessage && isRoomAvailable;
    const showSimilarHotelsSection = isLandingPage && (similarHotelsData === null || similarHotelsData === void 0 ? void 0 : similarHotelsData.carousel);
    const isUrgencyMessagingEnabled = isDestinationEnabledInConfig(searchState, UrgencyMessageDestinationEnabled, isLandingPage, isCacheEnabled) ||
        parseBoolean(EnableHotelUrgencyMessaging);
    useEffect(() => {
        if (typeof onTracking !== 'undefined') {
            onTracking(hotelDetails);
        }
    }, [hotelDetails, onTracking]);
    // Needed
    useMountEffect(() => {
        setStoredDepartureAirport();
        setStoredDestinationAirport();
        setConnectingFlight(null);
    });
    const handleShowReviewsDialog = React.useCallback((origin) => {
        setReviewsOrigin(origin);
        setReviewsDialogExited(false);
        handleOpenReviewsDialog();
    }, [handleOpenReviewsDialog]);
    const handleShowFlights = React.useCallback(() => {
        trackBetaEstabAction(BetaEstabAction.VIEW_MORE_FLIGHTS, TripType.Holiday);
        if (availabilityErrors.includes(HotelAvailabilityErrors.CHANGE_FLIGHT_ERROR))
            dispatch({
                type: HotelBasketActionType.REMOVE_AVAILABILITY_ERRORS,
                payload: HotelAvailabilityErrors.CHANGE_FLIGHT_ERROR,
            });
        setFlightsDrawerExited(false);
        handleOpenFlightsDrawer();
    }, [availabilityErrors, dispatch, handleOpenFlightsDrawer]);
    const showWeatherFlightRoute = isLandingPage && ((weatherRouteDetails === null || weatherRouteDetails === void 0 ? void 0 : weatherRouteDetails.routeData) || (weatherRouteDetails === null || weatherRouteDetails === void 0 ? void 0 : weatherRouteDetails.weatherData));
    const showStaticRooms = isLandingPage && ((_f = hotelDetails.masterRooms) === null || _f === void 0 ? void 0 : _f.length) > 0;
    const [stickyContextRef, setStickyContextRef] = React.useState(null);
    useEffect(() => {
        detailsDispatch({
            type: HotelDetailsActionType.SET_SCROLL_TO_SECTION,
            payload: handleScrollToSection,
        });
        if (!isValidEstablishmentPageQuery() && (waitingForRooms || waitingForFlights)) {
            dispatch({
                type: HotelBasketActionType.SET_WAITING_FOR_ROOM,
                payload: false,
            });
            dispatch({
                type: HotelBasketActionType.SET_WAITING_FOR_FLIGHT,
                payload: false,
            });
        }
    }, [handleScrollToSection, detailsDispatch, waitingForRooms, waitingForFlights, dispatch]);
    useEffect(() => {
        const isTripChanged = currentTripStateIdVariable !== prevTripState && prevTripState;
        // Reset the context on new trips i.e. new searches!
        if (isTripChanged) {
            dispatch({ type: HotelBasketActionType.RESET_CONTEXT });
            detailsDispatch({ type: HotelDetailsActionType.RESET_CONTEXT });
            toasts.clean(toastsStore);
        }
        if (displayPricingCalendar && !pricingCalendarInitialLoad) {
            detailsDispatch({
                type: HotelDetailsActionType.SET_PRICING_CALENDAR_TOGGLE,
                payload: displayPricingCalendar !== null && displayPricingCalendar !== void 0 ? displayPricingCalendar : false,
            });
        }
    }, [
        currentTripStateIdVariable,
        prevTripState,
        displayPricingCalendar,
        pricingCalendarInitialLoad,
        showPricingCalendar,
        dispatch,
        detailsDispatch,
    ]);
    // Used to log pricing differences to datadog
    useRecordPriceSummaryToCalendar(addDatadogRumAction);
    useRecordPriceSrpToSummary(addDatadogRumAction);
    // Prepare an array of page sections.
    // These are the main column sections below the gallery which are navigable using the scrollspy buttons.
    const pageSections = {};
    /* Hotel overview */
    pageSections.overview = {
        title: HOTEL_DETAILS_SCROLLSPY_OVERVIEW,
        element: (React.createElement(ScrollspyTarget, { component: "section", value: "overview", "data-id": "overview-section" },
            React.createElement(HotelOverview, { hotelDetails: hotelDetails }))),
    };
    /* Flight Upsell */
    if (showFlightUpsell) {
        pageSections.flightUpsell = {
            title: HOTEL_DETAILS_SCROLLSPY_FLIGHTUPSELL,
            element: (React.createElement(ScrollspyTarget, { component: "section", value: "flightUpsell", "data-id": "flight-upsell-section" },
                React.createElement(HotelFlightUpsell, { hotelDetails: hotelDetails, state: searchState }))),
        };
    }
    /* Hotel map  */
    pageSections.map = {
        title: HOTEL_DETAILS_SCROLLSPY_MAP,
        element: (React.createElement(ScrollspyTarget, { component: "section", value: "map", "data-id": "map-section" },
            React.createElement(LazyLoadComponent, { intersectionProps: { triggerOnce: true }, child: React.createElement(HotelStaticMap, { staticDetails: hotelDetails }) }))),
    };
    /* Summary (Mobile only) */
    if (isMobileLayout) {
        pageSections.summary = {
            title: HOTEL_DETAILS_SCROLLSPY_SUMMARY,
            element: (React.createElement(ScrollspyTarget, { component: "section", value: "summary", "data-id": "summary-section" }, isLandingPage ? (React.createElement(Paper, { component: "section", py: 5, defaultGutters: true, mt: 6 },
                React.createElement(HotelHeadline, { hotelDetails: hotelDetails, onReviewsDialogTriggerClick: handleShowReviewsDialog }))) : (React.createElement(HotelSummary, { hotelDetails: hotelDetails, onReviewsDialogTriggerClick: handleShowReviewsDialog, BasketProps: { withContentRegion: true } })))),
        };
    }
    /* Offers (LP only) */
    if (isMobileLayout && showHotelOffers) {
        pageSections.offers = {
            title: null,
            element: (React.createElement(Styler, { "data-default-gutters": true },
                React.createElement(HotelOffers, { hotelName: hotelDetails.name, estabId: hotelDetails.estabId, "data-id": "estab-offers-section" }))),
        };
    }
    /* Price calendar */
    if (showPricingCalendar) {
        pageSections.calendar = {
            title: HOTEL_DETAILS_SCROLLSPY_PACKAGE,
            element: (React.createElement(ScrollspyTarget, { component: "section", value: "calendar", "data-id": "calendar-section" }, isLandingPage ? (React.createElement(LandingPagePriceCalendar, { hotel: hotelDetails })) : (React.createElement(HotelPriceCalendar, { hotel: hotelDetails })))),
        };
    }
    /* Flight details (funnel only) */
    if (showFlightsSection) {
        pageSections.flights = {
            title: HOTEL_DETAILS_SCROLLSPY_FLIGHTS,
            element: (React.createElement(ScrollspyTarget, { component: "section", value: "flights", "data-id": "flight-section" },
                React.createElement(HotelFlightsSummary, { hotelDetails: hotelDetails, requiresBackgroundFlightSearch: requiresBackgroundFlightSearch, onClickChangeFlight: handleShowFlights }))),
        };
    }
    /* Hotel reviews */
    if (showReviews) {
        pageSections.reviews = {
            title: HOTEL_DETAILS_SCROLLSPY_REVIEWS,
            element: (React.createElement(ScrollspyTarget, { component: "section", value: "reviews", "data-id": "reviews-section" },
                React.createElement(HotelReviewsPageSection, { hotelDetails: hotelDetails, onDialogTriggerClick: handleShowReviewsDialog }))),
        };
    }
    /* Rooms */
    if (showRoomsComponent || showStaticRooms) {
        pageSections.rooms = {
            title: HOTEL_DETAILS_SCROLLSPY_ROOMS,
            element: (React.createElement(ScrollspyTarget, { component: "section", value: "rooms", "data-id": "rooms-section" }, showStaticRooms ? (React.createElement(HotelStaticRooms, { masterRooms: hotelDetails.masterRooms })) : (React.createElement(HotelRoomBoardSelector, { hotelSummaryResult: hotelSummaryResult, onSearchUnitOpenChange: onSearchUnitOpenChange })))),
        };
    }
    /* Weather and flight routes (LP only) */
    if (showWeatherFlightRoute) {
        pageSections.widgets = {
            title: null,
            element: (React.createElement("section", { "data-id": "weather-and-flight-routes" },
                React.createElement(WeatherFlightRoute, { weatherData: weatherRouteDetails === null || weatherRouteDetails === void 0 ? void 0 : weatherRouteDetails.weatherData, routeData: weatherRouteDetails === null || weatherRouteDetails === void 0 ? void 0 : weatherRouteDetails.routeData, "data-id": "weather-section" }))),
        };
    }
    const sectionOrder = useHotelDetailsPageSectionOrder(pageVariant);
    return (React.createElement(React.Fragment, null,
        React.createElement(Styler, { pb: showSimilarHotelsSection ? 0 : 20, mih: 500 },
            showNightsAdjustedNotification && (React.createElement(Container, null,
                React.createElement(NightsAdjustedNotification, { my: 5 }))),
            React.createElement(Container, { mb: 6, disableContentMargins: isMobileLayout },
                React.createElement(FlexGrid, { container: true, flexWrap: "nowrap" },
                    React.createElement(FlexGrid, { xs: "fill", mr: { xs: 0, l: 8 } },
                        React.createElement(Styler, { component: "section", pos: "relative" },
                            React.createElement(HotelImageGallery, { hotelDetails: hotelDetails, isSponsored: isSponsored, callout: ((_g = selectedRooms === null || selectedRooms === void 0 ? void 0 : selectedRooms[0]) === null || _g === void 0 ? void 0 : _g.pricingSummary.callout) || undefined, showUrgencyMessaging: isUrgencyMessagingEnabled, onReviewsDialogTriggerClick: handleShowReviewsDialog })),
                        React.createElement(Stacker, { spacing: { xs: 6, l: 8 }, mt: { xs: 0, l: 8 } },
                            React.createElement(Scrollspy, { ref: scrollspyRef },
                                !isLandingPage && (React.createElement(ScrollspyNavigation, { SurfaceProps: { radius: { xs: 'none', l: 's' } } }, sectionOrder.map((key) => {
                                    const section = pageSections[key];
                                    if (!section) {
                                        return null;
                                    }
                                    return (React.createElement(ScrollspyButton, { targetValue: key, "data-id": `${key}-tab`, key: key }, section.title));
                                }))),
                                React.createElement(Stacker, { spacing: { xs: 6, l: 8 } }, sectionOrder.map((key) => {
                                    const section = pageSections[key];
                                    if (!section) {
                                        return null;
                                    }
                                    return React.createElement(React.Fragment, { key: key }, section.element);
                                }))))),
                    !isMobileLayout && (React.createElement(FlexGrid, { l: 4, flexBasis: SUMMARY_COLUMN_MIN_WIDTH, miw: SUMMARY_COLUMN_MIN_WIDTH, "data-id": "estab-summary-pannel" },
                        React.createElement(Stacker, { spacing: 4, h: "100%", ref: (ref) => setStickyContextRef(ref) },
                            isLandingPage && (React.createElement(React.Fragment, null,
                                React.createElement(Paper, { component: "section", py: 5, defaultGutters: true },
                                    React.createElement(HotelHeadline, { hotelDetails: hotelDetails, onReviewsDialogTriggerClick: handleShowReviewsDialog })),
                                showHotelOffers && (React.createElement(HotelOffers, { hotelName: hotelDetails.name, estabId: hotelDetails.estabId, isSticky: true, stickyContext: stickyContextRef || undefined, pt: 4 })))),
                            !isLandingPage && (React.createElement(HotelSummary, { hotelDetails: hotelDetails, isSticky: true, onReviewsDialogTriggerClick: handleShowReviewsDialog, BasketProps: { withCTA: true, withContentRegion: true }, stickyContext: stickyContextRef || undefined }))))))),
            showSimilarHotelsSection && React.createElement(SimilarHotels, { similarHotelsDetails: similarHotelsData })),
        (flightsDrawerOpen || !flightsDrawerExited) && (React.createElement(HotelFlightsDrawerMemo, { open: flightsDrawerOpen, onClose: handleCloseFlightsDrawer, openDrawer: handleOpenFlightsDrawer })),
        (reviewsDialogOpen || !reviewsDialogExited) && (React.createElement(HotelReviewsDialog, { hotel: hotelDetails, origin: reviewsOrigin, open: reviewsDialogOpen, onClose: handleCloseReviewsDialog, onExited: () => setReviewsDialogExited(true) })),
        React.createElement(HotelDetailsRichResult, { hotelStaticDetails: hotelDetails }),
        isMobileLayout && !isLandingPage && React.createElement(HotelMobileFooterBar, null)));
}
